import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { X } from "lucide-react";
import {
  TextInputField,
  NumberInputField,
  SelectField,
  FormSubmitButton,
  unitOptions,
} from "@/components/forms/FormComponents";
// apiClient and mutations are handled by parent component
import { toastHelpers } from "@/lib/toast-helpers";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { useState, useEffect } from "react";
import { Stock } from "@/types";

// Stock item form schema - aligned with Sequelize Stock model
const stockItemSchema = z.object({
  name: z.string().min(1, "Name is required"),
  unit: z.string().min(1, "Unit is required"),
  // Use z.coerce.number() so string input from number fields is coerced to numbers
  quantity: z.coerce.number().min(0, "Quantity must be 0 or greater"),
  reserved_quantity: z.coerce
    .number()
    .min(0, "Reserved quantity must be 0 or greater"),
  low_stock_threshold: z.coerce
    .number()
    .min(0, "Low stock threshold must be 0 or greater"),
  cost_per_unit: z.coerce.number().min(0, "Cost per unit must be 0 or greater"),
  supplier: z.string().min(1, "Supplier is required"),
  supplier_contact: z.string().min(1, "Supplier contact is required"),
  last_restocked_at: z.string().nullable().optional(),
  image_url: z.string().optional(),
  category_id: z.string().optional(),
});

type StockItemFormData = z.infer<typeof stockItemSchema>;

interface StockItemFormProps {
  stockItem?: Stock;
  onSuccess?: () => void; // kept for compatibility
  onCancel?: () => void;
  mode?: "create" | "edit";
  // handlers injected by parent
  createHandler?: (data: any) => void;
  updateHandler?: (id: String, data: any) => void;
  isSubmitting?: boolean;
}

export function StockItemForm({
  stockItem,
  onCancel,
  mode = "create",
  // New handlers passed from parent
  createHandler,
  updateHandler,
  isSubmitting,
}: StockItemFormProps) {
  const { t } = useTranslation();
  const isEditing = mode === "edit" && stockItem;

  // Image preview state (matching ProductForm pattern)
  const [previewUrl, setPreviewUrl] = useState<string>(
    stockItem?.image_url || ""
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Fetch categories for dropdown
  const { data: categoriesResponse } = useQuery({
    queryKey: ["categories"],
    queryFn: () => apiClient.getCategories(),
  });

  const categories = categoriesResponse?.data || [];

  // Default values for the form - using backend field names
  const defaultValues = isEditing
    ? {
        name: stockItem.name,
        unit: stockItem.unit,
        quantity: stockItem.quantity,
        reserved_quantity: stockItem.reserved_quantity,
        low_stock_threshold: stockItem.low_stock_threshold,
        cost_per_unit: stockItem.cost_per_unit,
        supplier: stockItem.supplier,
        supplier_contact: stockItem.supplier_contact,
        last_restocked_at: stockItem.last_restocked_at || null,
        image_url: stockItem.image_url || "",
        category_id: stockItem.category_id?.toString() || undefined,
      }
    : {
        name: "",
        unit: "",
        quantity: 0,
        reserved_quantity: 0,
        low_stock_threshold: 0,
        cost_per_unit: 0,
        supplier: "",
        supplier_contact: "",
        last_restocked_at: null,
        image_url: "",
        category_id: undefined,
      };

  const form = useForm<StockItemFormData>({
    resolver: zodResolver(stockItemSchema),
    defaultValues,
  });

  // Unit -> step mapping
  const unitStepMap: Record<string, number> = {
    // General units
    pcs: 1,
    piece: 1,
    pieces: 1,
    dozen: 1,
    kg: 0.01,
    g: 1,
    lb: 0.001,
    oz: 0.001,
    l: 0.001,
    ml: 1,
    boxes: 1,
    cans: 1,
    bottles: 1,
    bags: 1,
    rolls: 1,
    sheets: 1,
    units: 1,
    // Dental-specific units
    vials: 1,
    ampoules: 1,
    syringes: 1,
    cartridges: 1,
    tubes: 1,
    jars: 1,
    packets: 1,
    strips: 1,
    pads: 1,
    swabs: 1,
    applicators: 1,
    tips: 1,
    needles: 1,
    burs: 1,
    files: 1,
    gloves_pairs: 1,
    masks: 1,
    gowns: 1,
    gauze: 1,
    cotton_rolls: 1,
    impression_trays: 1,
    crowns: 1,
    bridges: 1,
    implants: 1,
    sutures: 1,
    packaged_sets: 1,
    default: 0.001,
  };

  const selectedUnit = form.watch("unit") || "pcs";
  const numericStep = unitStepMap[selectedUnit] ?? unitStepMap.default;

  // Update preview URL when form image_url changes (for editing existing stock items)
  useEffect(() => {
    const imageUrl = form.getValues("image_url");
    if (imageUrl && imageUrl !== previewUrl) {
      setPreviewUrl(imageUrl);
    }
  }, [form.watch("image_url")]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // createHandler/updateHandler passed from parent will be used instead of local mutations

  const onSubmit = async (data: StockItemFormData) => {
    try {
      // Map directly to backend Stock model fields
      let payload = {
        name: data.name,
        unit: data.unit,
        quantity: Number(data.quantity),
        reserved_quantity: Number(data.reserved_quantity),
        low_stock_threshold: Number(data.low_stock_threshold),
        cost_per_unit: Number(data.cost_per_unit),
        supplier: data.supplier,
        supplier_contact: data.supplier_contact,
        last_restocked_at: data.last_restocked_at,
        image_url: data.image_url || null,
        category_id: data.category_id ? Number(data.category_id) : null,
      };

      // If there's a new file to upload, upload it first (matching ProductForm pattern)
      if (selectedFile) {
        setIsUploadingImage(true);
        try {
          const response = await apiClient.uploadImage(selectedFile);
          const imageUrl = response.data.url;

          if (!imageUrl) {
            throw new Error("Upload failed - no image URL returned");
          }

          // Update payload with the uploaded image URL
          payload = { ...payload, image_url: imageUrl };

          // Clean up the blob URL and selected file
          if (previewUrl && previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(previewUrl);
          }
          setSelectedFile(null);
          setPreviewUrl(imageUrl);
        } catch (uploadError) {
          setIsUploadingImage(false);
          toastHelpers.apiError("Image upload", uploadError);
          return; // Don't proceed with stock creation if image upload fails
        } finally {
          setIsUploadingImage(false);
        }
      }

      // Now proceed with stock creation/update
      if (isEditing) {
        if (updateHandler && stockItem) updateHandler(stockItem.id, payload);
      } else {
        if (createHandler) createHandler(payload);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toastHelpers.apiError("Form submission", error);
    }
  };

  const isLoading = isSubmitting || isUploadingImage;

  return (
    <Card className="w-full max-w-4xl mx-auto shadow-lg border-0">
      <CardHeader className="flex flex-row items-center justify-between border-b">
        <CardTitle className="text-2xl font-bold text-gray-900">
          {isEditing
            ? t("StockItemForm.Title.Edit")
            : t("StockItemForm.Title.Add")}
        </CardTitle>
        {onCancel && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            disabled={isLoading}
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {/* Basic Information */}
            <div className="space-y-6">
              <div className="border-l-4 border-blue-500 pl-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t("StockItemForm.Sections.StockInformation")}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <TextInputField
                  control={form.control}
                  name="name"
                  label={t("StockItemForm.Fields.Name.Label")}
                  placeholder={t("StockItemForm.Fields.Name.Placeholder")}
                  description={t("StockItemForm.Fields.Name.Description")}
                />

                <SelectField
                  control={form.control}
                  name="unit"
                  label={t("StockItemForm.Fields.Unit.Label")}
                  options={unitOptions}
                  placeholder={t("StockItemForm.Fields.Unit.Placeholder")}
                  description={t("StockItemForm.Fields.Unit.Description")}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SelectField
                  control={form.control}
                  name="category_id"
                  label={t("StockItemForm.Fields.Category.Label")}
                  options={categories.map((cat) => ({
                    value: cat.id.toString(),
                    label: cat.name,
                  }))}
                  placeholder={t("StockItemForm.Fields.Category.Placeholder")}
                  description={t("StockItemForm.Fields.Category.Description")}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <TextInputField
                  control={form.control}
                  name="supplier"
                  label={t("StockItemForm.Fields.Supplier.Label")}
                  placeholder={t("StockItemForm.Fields.Supplier.Placeholder")}
                  description={t("StockItemForm.Fields.Supplier.Description")}
                />
              </div>

              <TextInputField
                control={form.control}
                name="supplier_contact"
                label={t("StockItemForm.Fields.SupplierContact.Label")}
                placeholder={t(
                  "StockItemForm.Fields.SupplierContact.Placeholder"
                )}
                description={t(
                  "StockItemForm.Fields.SupplierContact.Description"
                )}
              />
            </div>

            {/* Image Upload Section */}
            <div className="space-y-6">
              <div className="border-l-4 border-purple-500 pl-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t("StockItemForm.Sections.Image")}
                </h2>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t("StockItemForm.Fields.ImageUpload.Label")}
                </label>
                <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-1 hover:border-gray-400 transition-colors">
                  <input
                    id="stock-image-input"
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-0"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      try {
                        // Store the selected file for later upload
                        setSelectedFile(file);

                        // Create local preview URL
                        const localPreviewUrl = URL.createObjectURL(file);
                        setPreviewUrl(localPreviewUrl);

                        // Clear the image_url field since we haven't uploaded yet
                        form.setValue("image_url", "");
                      } catch (err) {
                        console.error("File selection failed", err);
                        toastHelpers.apiError("File selection", err);
                      }
                    }}
                  />

                  {previewUrl ? (
                    // Show preview image in upload area
                    <div className="text-center">
                      <div className="relative inline-block">
                        <img
                          src={previewUrl}
                          alt="Stock preview"
                          className="h-32 w-32 object-cover rounded-lg border-2 border-gray-200 shadow-sm mx-auto"
                        />
                      </div>
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-900">
                          {selectedFile
                            ? t(
                                "StockItemForm.Fields.ImageUpload.ReadyToUpload"
                              )
                            : t("StockItemForm.Fields.ImageUpload.Uploaded")}
                        </p>
                        <p className="text-xs text-gray-500 mb-3">
                          {selectedFile
                            ? t("StockItemForm.Fields.ImageUpload.WillUpload")
                            : t(
                                "StockItemForm.Fields.ImageUpload.CurrentImage"
                              )}
                        </p>
                      </div>
                      <div className="flex gap-2 justify-center">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            const fileInput = document.getElementById(
                              "stock-image-input"
                            ) as HTMLInputElement | null;
                            fileInput?.click();
                          }}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200 hover:border-blue-300 relative z-10"
                        >
                          {t("StockItemForm.Fields.ImageUpload.ChangeImage")}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Clean up blob URL if it exists
                            if (previewUrl && previewUrl.startsWith("blob:")) {
                              URL.revokeObjectURL(previewUrl);
                            }
                            // Clear the file input value
                            const fileInput = document.getElementById(
                              "stock-image-input"
                            ) as HTMLInputElement | null;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                            // Clear image_url and state
                            form.setValue("image_url", "");
                            setPreviewUrl("");
                            setSelectedFile(null);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300 relative z-10"
                        >
                          {t("StockItemForm.Fields.ImageUpload.RemoveImage")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Show upload prompt when no image
                    <div className="text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="mt-2">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium text-gray-900 hover:text-gray-700">
                            {t(
                              "StockItemForm.Fields.ImageUpload.ClickToUpload"
                            )}
                          </span>{" "}
                          {t("StockItemForm.Fields.ImageUpload.OrDragDrop")}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t("StockItemForm.Fields.ImageUpload.FileTypes")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Stock & Inventory */}
            <div className="space-y-6">
              <div className="border-l-4 border-green-500 pl-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t("StockItemForm.Sections.StockAndPricing")}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <NumberInputField
                  control={form.control}
                  name="quantity"
                  label={t("StockItemForm.Fields.Quantity.Label")}
                  min={0}
                  step={numericStep}
                  description={t("StockItemForm.Fields.Quantity.Description")}
                />

                <NumberInputField
                  control={form.control}
                  name="reserved_quantity"
                  label={t("StockItemForm.Fields.ReservedQuantity.Label")}
                  min={0}
                  step={numericStep}
                  description={t(
                    "StockItemForm.Fields.ReservedQuantity.Description"
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <NumberInputField
                  control={form.control}
                  name="low_stock_threshold"
                  label={t("StockItemForm.Fields.LowStockThreshold.Label")}
                  min={0}
                  step={numericStep}
                  description={t(
                    "StockItemForm.Fields.LowStockThreshold.Description"
                  )}
                />

                {/* critical_stock_threshold removed - using only low_stock_threshold for alerts */}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <NumberInputField
                  control={form.control}
                  name="cost_per_unit"
                  label={t("StockItemForm.Fields.CostPerUnit.Label")}
                  min={0}
                  step={0.01}
                  description={t(
                    "StockItemForm.Fields.CostPerUnit.Description"
                  )}
                />
              </div>
            </div>

            {/* Additional Details */}
            <div className="space-y-6">
              <div className="border-l-4 border-purple-500 pl-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t("StockItemForm.Sections.AdditionalDetails")}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="text-sm text-gray-600">
                  <strong>{t("StockItemForm.Info.LastRestocked")}:</strong>{" "}
                  {isEditing && stockItem.last_restocked_at
                    ? new Date(stockItem.last_restocked_at).toLocaleDateString()
                    : t("StockItemForm.Info.Never")}
                </div>
                <div className="text-sm text-gray-600">
                  <strong>{t("StockItemForm.Info.Created")}:</strong>{" "}
                  {isEditing
                    ? new Date(stockItem.created_at).toLocaleDateString()
                    : t("StockItemForm.Info.NewItem")}
                </div>
              </div>

              {isEditing && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-sm text-gray-600">
                    <strong>{t("StockItemForm.Info.LastUpdated")}:</strong>{" "}
                    {new Date(stockItem.updated_at).toLocaleDateString()}
                  </div>
                  <div className="text-sm text-gray-600">
                    <strong>{t("StockItemForm.Info.UpdatedAt")}:</strong>{" "}
                    {new Date(stockItem.updated_at).toLocaleTimeString()}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-6 border-t border-gray-200">
              <FormSubmitButton
                isLoading={isLoading}
                loadingText={
                  isEditing
                    ? t("StockItemForm.Actions.Updating")
                    : t("StockItemForm.Actions.Creating")
                }
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3"
              >
                {isEditing
                  ? t("StockItemForm.Actions.UpdateStock")
                  : t("StockItemForm.Actions.CreateStock")}
              </FormSubmitButton>

              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading}
                  className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-3"
                >
                  {t("StockItemForm.Actions.Cancel")}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
